import { useState, useMemo } from "react";
import { TourismPackage } from "@/components/tourism-packages/types";
import { AdvancedFilters } from "@/components/tourism-packages/PackageFiltersAdvanced";

const defaultFilters: AdvancedFilters = {
  search: '',
  countryId: 'all',
  cityId: 'all',
  priceMin: 0,
  priceMax: 100000,
  duration: 'all',
  rating: 0,
  status: 'all',
  type: 'all',
  paymentStatus: 'all',
  services: [],
};

export const usePackageFilters = (packages: TourismPackage[]) => {
  const [filters, setFilters] = useState<AdvancedFilters>(defaultFilters);
  const [sortBy, setSortBy] = useState<string>('latest');

  const filteredPackages = useMemo(() => {
    let filtered = [...packages];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (pkg) =>
          pkg.title.toLowerCase().includes(searchLower) ||
          pkg.destination.toLowerCase().includes(searchLower) ||
          pkg.package_number.toLowerCase().includes(searchLower)
      );
    }

    // Country filter (check destination string since country_id may not exist)
    if (filters.countryId && filters.countryId !== 'all') {
      filtered = filtered.filter((pkg) => {
        // Match by destination or package_details
        const details = pkg.package_details as any;
        return details?.countryId === filters.countryId || 
               pkg.destination?.includes(filters.countryId);
      });
    }

    // City filter
    if (filters.cityId && filters.cityId !== 'all') {
      filtered = filtered.filter((pkg) => {
        const details = pkg.package_details as any;
        return details?.cityId === filters.cityId ||
               pkg.destination?.includes(filters.cityId);
      });
    }

    // Price range filter
    filtered = filtered.filter(
      (pkg) => pkg.price >= filters.priceMin && pkg.price <= filters.priceMax
    );

    // Duration filter
    if (filters.duration && filters.duration !== 'all') {
      filtered = filtered.filter((pkg) => {
        const nights = parseInt(pkg.duration) || 0;
        switch (filters.duration) {
          case 'short':
            return nights <= 3;
          case 'medium':
            return nights >= 4 && nights <= 7;
          case 'long':
            return nights >= 8;
          default:
            return true;
        }
      });
    }

    // Rating filter
    if (filters.rating > 0) {
      filtered = filtered.filter((pkg) => (pkg.rating || 0) >= filters.rating);
    }

    // Status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter((pkg) => pkg.status === filters.status);
    }

    // Type filter
    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter((pkg) => pkg.type === filters.type);
    }

    // Payment Status filter
    if (filters.paymentStatus && filters.paymentStatus !== 'all') {
      filtered = filtered.filter((pkg) => pkg.payment_status === filters.paymentStatus);
    }

    // Services filter
    if (filters.services.length > 0) {
      filtered = filtered.filter((pkg) => {
        const details = pkg.package_details as any;
        if (!details) return false;

        return filters.services.every((service) => {
          switch (service) {
            case 'الفنادق':
              return details.includesHotels;
            case 'الطيران الدولي':
              return details.includesInternationalFlights;
            case 'الطيران الداخلي':
              return details.includesDomesticFlights;
            case 'المواصلات':
              return details.includesTransport;
            case 'الجولات السياحية':
              return details.includesTours;
            case 'الخدمات الإضافية':
              return details.includesServices;
            default:
              return true;
          }
        });
      });
    }

    return filtered;
  }, [packages, filters]);

  const sortedPackages = useMemo(() => {
    const sorted = [...filteredPackages];

    switch (sortBy) {
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'rating':
        return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      case 'popular':
        return sorted.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
      case 'latest':
      default:
        return sorted.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }
  }, [filteredPackages, sortBy]);

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSortBy('latest');
  };

  return {
    filters,
    setFilters,
    sortBy,
    setSortBy,
    filteredPackages: sortedPackages,
    resetFilters,
    totalResults: sortedPackages.length,
  };
};
